
import {
  GQL_LANDING_POPULAR_PRODUCTS,
  GQL_LANDING_PRODUCTS,
  GQL_LANDING_SALE_PRODUCTS,
} from '@features/landing-page-builder/model/productQueries'

import {
  IMAGE_SIZES,
  URL_CATEGORY_NEW_PRODUCTS,
  URL_CATEGORY_POPULAR_PRODUCTS,
  URL_CATEGORY_SALE_PRODUCTS,
} from '@shared/contants'

import type { DocumentNode } from '@apollo/client'

/*
  A landing `products` block describes a standard catalog request:
    payload.query      which backend query to call
    payload.categoryId / payload.filter   what to narrow it to
    payload.pager / payload.limit / payload.sort
  `subtype` is the fallback when payload.query is absent (older seeds).
*/
export interface LandingProductsPayload {
  name?: string;
  title?: string;
  tabLabel?: string;
  query?: string;
  categoryId?: string | number;
  filter?: Record<string, unknown>;
  pager?: { page?: number; limit?: number };
  sort?: { fieldName?: string; direction?: number };
  limit?: number;
  displayMode?: string;
  /** products/group carries the group id (single) or a list (see groups). */
  id?: string | number;
}

export interface ResolvedProductsQuery {
  document: DocumentNode;
  /** Path to the edges inside the response. */
  root: 'products' | 'popularProducts' | 'saleProducts';
  variables: Record<string, unknown>;
  /** "See all" destination for the section header. */
  link?: string;
}

const DEFAULT_LIMIT = 10

/*
  "See all" leads to a standard catalog list, which shows the whole shop by
  default. A landing block is usually scoped (a category, an authored filter),
  so the scope travels in the query string and the destination page restores it
  — otherwise the visitor jumps from "Скидки" on a school landing to every
  discount in the shop.

  The filter is carried verbatim as JSON: it is the same NestedFilterInput the
  block itself issues, so no shape is lost in translation.
*/
export const PRODUCTS_SCOPE_PARAM = {
  categoryId: 'categoryId',
  filter: 'filter',
} as const

export function buildProductsScopeSearch (
  categoryId?: string,
  filter?: Record<string, unknown>,
): string {
  const params = new URLSearchParams()

  if (categoryId) {
    params.set(PRODUCTS_SCOPE_PARAM.categoryId, categoryId)
  }

  if (filter && Object.keys(filter).length > 0) {
    params.set(PRODUCTS_SCOPE_PARAM.filter, JSON.stringify(filter))
  }

  const search = params.toString()

  return search ? `?${search}` : ''
}

/*
  Reads back what buildProductsScopeSearch wrote. Tolerant by design: a
  hand-edited or truncated URL degrades to an unscoped list rather than throwing
  on the catalog page.
*/
export function readProductsScope (search: string): {
  categoryId?: string;
  filter?: Record<string, unknown>;
} {
  const params = new URLSearchParams(search)

  const categoryId = params.get(PRODUCTS_SCOPE_PARAM.categoryId) || undefined
  const rawFilter = params.get(PRODUCTS_SCOPE_PARAM.filter)

  if (!rawFilter) {
    return { categoryId }
  }

  try {
    const parsed = JSON.parse(rawFilter)

    return {
      categoryId,
      filter: parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : undefined,
    }
  }
  catch {
    return { categoryId }
  }
}

/*
  `filter` is passed through as authored — the backend narrows every list query
  (including sale, via Elasticsearch) from it.

  `categoryId` is kept separate because `products` also accepts a dedicated
  category_id argument. When only a category-scoped filter is given, its first
  value doubles as that argument so the plain `products` query is narrowed too.
*/
function resolveScope (payload: LandingProductsPayload): {
  categoryId?: string;
  filter?: Record<string, unknown>;
} {
  const filter = payload.filter ?? undefined
  const fields = Array.isArray(filter?.fields) ? (filter?.fields as unknown[]).map(String) : []
  const isCategoryFilter = fields.length > 0 && fields.every((field) => field === 'category_id')
  const values = Array.isArray(filter?.value) ? (filter?.value as unknown[]).map(String) : []

  const categoryId = payload.categoryId != null
    ? String(payload.categoryId)
    : (isCategoryFilter ? values[0] : undefined)

  return { categoryId, filter }
}

export function resolveProductsQuery (
  subtype: string | null | undefined,
  payload: LandingProductsPayload | null,
  shopId: string,
): ResolvedProductsQuery | null {
  const p = payload ?? {}
  const kind = p.query || subtypeToQuery(subtype)

  if (!kind) {
    return null
  }

  const { categoryId, filter } = resolveScope(p)

  const pager = {
    page: p.pager?.page ?? 1,
    limit: p.pager?.limit ?? p.limit ?? DEFAULT_LIMIT,
  }

  const base = { shopId, style: IMAGE_SIZES.TABLET, pager }

  // Carries the block's scope to the "see all" page so the list stays narrowed.
  const scope = buildProductsScopeSearch(categoryId, filter)

  switch (kind) {
  case 'popularProducts':
    return {
      document: GQL_LANDING_POPULAR_PRODUCTS,
      root: 'popularProducts',
      variables: { ...base, filter },
      link: `${URL_CATEGORY_POPULAR_PRODUCTS}${scope}`,
    }

  case 'saleProducts':
    return {
      document: GQL_LANDING_SALE_PRODUCTS,
      root: 'saleProducts',
      variables: { ...base, filter },
      link: `${URL_CATEGORY_SALE_PRODUCTS}${scope}`,
    }

  case 'products': {
    /*
      A `new` block is just `products` sorted by novelty. Older seeds relied on
      the subtype alone to imply that sort, so it is applied when the payload
      does not spell it out.
    */
    const sort = p.sort?.fieldName
      ? p.sort
      : (subtype === 'new' ? { fieldName: 'new', direction: 0 } : undefined)

    return {
      document: GQL_LANDING_PRODUCTS,
      root: 'products',
      variables: { ...base, categoryId, filter, sort },
      // A novelty-sorted products request is the catalog's "new" page.
      link: sort?.fieldName === 'new' ? `${URL_CATEGORY_NEW_PRODUCTS}${scope}` : undefined,
    }
  }

  default:
    return null
  }
}

/*
  Legacy seeds carry no payload.query — the subtype alone said which standard
  list to show. Kept so older landings keep working.
*/
function subtypeToQuery (subtype?: string | null): string | null {
  switch (subtype) {
  case 'popular':
    return 'popularProducts'
  case 'sale':
    return 'saleProducts'
  case 'new':
    return 'products'
  default:
    return null
  }
}
